// =============================================================================
// ACT SCIENCE TEST BANK - Form J08
// Source: ACT October 2025 Release
// Total Questions: 40 (34 scored, 6 field test removed in this version)
// =============================================================================

export const formId = 'J08';
export const subject = 'science';
export const totalQuestions = 34; // Scored questions only
export const scoredQuestions = 34;
export const timeLimit = 35; // minutes (for scored questions only)
export const standardTime = 40; // Full test time

// Field test questions were removed from this release
export const fieldTestPositions = [];

// -----------------------------------------------------------------------------
// SCORING TABLE (34 scored questions → 1-36 scale)
// -----------------------------------------------------------------------------
export const scoringTable = {
  34: 36, 33: 36, 32: 35, 31: 34, 30: 33, 29: 32, 28: 31, 27: 29, 26: 28,
  25: 27, 24: 26, 23: 25, 22: 25, 21: 24, 20: 23, 19: 23, 18: 22, 17: 21,
  16: 20, 15: 19, 14: 18, 13: 17, 12: 16, 11: 16, 10: 15, 9: 14, 8: 13,
  7: 12, 6: 12, 5: 11, 4: 10, 3: 8, 2: 6, 1: 4, 0: 1
};

// -----------------------------------------------------------------------------
// CATEGORY DEFINITIONS
// -----------------------------------------------------------------------------
export const categories = {
  IOD: { name: 'Interpretation of Data', description: 'Manipulate and analyze data in tables, graphs, and diagrams', count: 16 },
  SIN: { name: 'Scientific Investigation', description: 'Understand experimental tools, procedures, and design', count: 8 },
  EMI: { name: 'Evaluation of Models, Inferences & Experimental Results', description: 'Judge validity of information and formulate conclusions', count: 10 }
};

// -----------------------------------------------------------------------------
// PASSAGES
// -----------------------------------------------------------------------------
export const passages = [
  {
    id: 'J08-S1',
    title: 'Sound Waves in Salt Water',
    format: 'Research Summaries',
    topic: 'Physics - Acoustics',
    questionRange: [1, 6],
    description: 'Two experiments studying the frequency of sound waves produced when tapping a beaker containing salt water solution at different heights and temperatures.',
    dataElements: ['Figure 1: Experimental setup', 'Figure 2: Frequency vs time graph', 'Table 1: Frequency at different water heights']
  },
  {
    id: 'J08-S2',
    title: 'Lightning Discharges',
    format: 'Data Representation',
    topic: 'Earth Science - Meteorology',
    questionRange: [7, 12],
    description: 'Observations of lightning discharges collected during 10 thunderstorms, including storm duration, total discharges, flash rates, and charge distribution.',
    dataElements: ['Table 1: Storm data', 'Figure 1: Storm 6 discharge distribution', 'Figure 2: Charge distribution histogram']
  },
  {
    id: 'J08-S3',
    title: 'Blood Glucose Regulation',
    format: 'Conflicting Viewpoints',
    topic: 'Biology - Physiology',
    questionRange: [13, 18],
    description: 'Four students discuss the release of insulin, glucagon, and somatostatin from pancreatic cells and their roles in blood glucose regulation.',
    dataElements: ['Figure 1: Hormone release vs BG concentration graph']
  },
  {
    id: 'J08-S4',
    title: 'Phenol Removal from Wastewater',
    format: 'Research Summaries',
    topic: 'Chemistry - Environmental Science',
    questionRange: [19, 24],
    description: 'Three studies examining how pH, hydrogen peroxide volume, and reaction time affect the removal of phenol from wastewater.',
    dataElements: ['Figure 1: pH vs phenol removal', 'Figure 2: H2O2 vs phenol removal (1 hr)', 'Figure 3: H2O2 vs phenol removal (2 hr)']
  },
  {
    id: 'J08-S5',
    title: 'Mass Spectrometer Ion Studies',
    format: 'Research Summaries',
    topic: 'Physics - Instrumentation',
    questionRange: [25, 29],
    description: 'Four studies examining the magnetic field intensity required to achieve specific orbital radii for lithium, beryllium, and boron ions in a mass spectrometer.',
    dataElements: ['Diagram: MS setup', 'Tables 1-4: Ion trials with varying V and R']
  },
  {
    id: 'J08-S6',
    title: 'Seahorse Growth and Survival',
    format: 'Data Representation',
    topic: 'Biology - Marine Science',
    questionRange: [30, 34],
    description: 'Study of Hippocampus kuda seahorse growth rates during nursery and grow-out stages, and survival rates with different diet enrichments.',
    dataElements: ['Figure 1: Seahorse anatomy', 'Figure 2: Growth and mass over time', 'Table 1: Diet enrichments', 'Figure 3: Survival rates']
  }
];

// -----------------------------------------------------------------------------
// QUESTIONS
// -----------------------------------------------------------------------------
export const questions = [
  // PASSAGE I: Sound Waves in Salt Water (Q1-6)
  {
    id: 'J08-S01',
    formId: 'J08',
    questionNumber: 1,
    passageId: 'J08-S1',
    category: 'IOD',
    isScored: true,
    question: 'The speed of sound is directly proportional to the frequency of the sound wave. Based on the results of Experiment 1, how did the speed of sound vary, if at all, between 0 s and 120 s? The speed of sound:',
    choices: [
      { letter: 'A', text: 'increased and then decreased.' },
      { letter: 'B', text: 'increased and then remained constant.' },
      { letter: 'C', text: 'decreased and then remained constant.' },
      { letter: 'D', text: 'did not vary.' }
    ],
    correctIndex: 1,
    correctLetter: 'B',
    explanation: 'Figure 2 shows frequency increased from about 2,400 Hz at 0s to about 3,600 Hz at 90s, then leveled off. Since speed is proportional to frequency, speed increased then remained constant.'
  },
  {
    id: 'J08-S02',
    formId: 'J08',
    questionNumber: 2,
    passageId: 'J08-S1',
    category: 'IOD',
    isScored: true,
    question: 'The musical note A6 has a frequency of 1,760 Hz. How many of the frequencies listed in Table 1 are higher than the note A6?',
    choices: [
      { letter: 'F', text: '5' },
      { letter: 'G', text: '6' },
      { letter: 'H', text: '10' },
      { letter: 'J', text: '12' }
    ],
    correctIndex: 2,
    correctLetter: 'H',
    explanation: 'Looking at Table 1, all 12 values are above 1,760 Hz except 1,692 Hz and 1,480 Hz. That leaves 10 frequencies higher than A6.'
  },
  {
    id: 'J08-S03',
    formId: 'J08',
    questionNumber: 3,
    passageId: 'J08-S1',
    category: 'SIN',
    isScored: true,
    question: 'Based on the results of Experiments 1 and 2, if the procedures of Experiment 1 had been repeated and the frequency of the sound wave at 60 s had been 2,300 Hz, the height of water in the beaker would most likely have been:',
    choices: [
      { letter: 'A', text: 'less than 10 cm.' },
      { letter: 'B', text: 'between 12 cm and 14 cm.' },
      { letter: 'C', text: 'between 14 cm and 16 cm.' },
      { letter: 'D', text: 'greater than 16 cm.' }
    ],
    correctIndex: 2,
    correctLetter: 'C',
    explanation: 'At 60s, Table 1 shows: 12cm→2,918 Hz, 14cm→2,503 Hz, 16cm→2,188 Hz. A frequency of 2,300 Hz falls between 14cm (2,503) and 16cm (2,188).'
  },
  {
    id: 'J08-S04',
    formId: 'J08',
    questionNumber: 4,
    passageId: 'J08-S1',
    category: 'IOD',
    isScored: true,
    question: 'In Experiment 1, during which of the following time intervals was the average rate of change in frequency least?',
    choices: [
      { letter: 'F', text: 'From 0 s to 10 s' },
      { letter: 'G', text: 'From 20 s to 30 s' },
      { letter: 'H', text: 'From 40 s to 50 s' },
      { letter: 'J', text: 'From 60 s to 70 s' }
    ],
    correctIndex: 3,
    correctLetter: 'J',
    explanation: 'From Figure 2, the curve flattens out over time. The 60-70s interval shows the smallest vertical change (nearly flat), indicating the least rate of change in frequency.'
  },
  {
    id: 'J08-S05',
    formId: 'J08',
    questionNumber: 5,
    passageId: 'J08-S1',
    category: 'SIN',
    isScored: true,
    question: 'In which of the experiments, if either, did the scientist vary the rate of tapping the base of the beaker?',
    choices: [
      { letter: 'A', text: 'Experiment 1 only' },
      { letter: 'B', text: 'Experiment 2 only' },
      { letter: 'C', text: 'Both Experiment 1 and Experiment 2' },
      { letter: 'D', text: 'Neither Experiment 1 nor Experiment 2' }
    ],
    correctIndex: 3,
    correctLetter: 'D',
    explanation: 'The passage states the scientist "tapped the base of the beaker for 2 minutes at a constant rate." Experiment 2 repeated the procedures, so neither varied the tapping rate.'
  },
  {
    id: 'J08-S06',
    formId: 'J08',
    questionNumber: 6,
    passageId: 'J08-S1',
    category: 'SIN',
    isScored: true,
    question: 'Based on the procedures of Experiment 1 and Experiment 2, what was the height of the water when the water had the least concentration of salt?',
    choices: [
      { letter: 'F', text: '10 cm' },
      { letter: 'G', text: '12 cm' },
      { letter: 'H', text: '14 cm' },
      { letter: 'J', text: '16 cm' }
    ],
    correctIndex: 3,
    correctLetter: 'J',
    explanation: 'The same 10g of salt was added to each trial. More water (greater height) means lower concentration. 16 cm has the most water and thus the least salt concentration.'
  },

  // PASSAGE II: Lightning Discharges (Q7-12)
  {
    id: 'J08-S07',
    formId: 'J08',
    questionNumber: 7,
    passageId: 'J08-S2',
    category: 'IOD',
    isScored: true,
    question: 'Based on Figure 1, the greatest number of lightning discharges occurred during which time period, and approximately how many lightning discharges were observed during that time period?',
    choices: [
      { letter: 'A', text: 'Time period: 4:55 p.m. to 5:00 p.m. Number of discharges: 85' },
      { letter: 'B', text: 'Time period: 4:55 p.m. to 5:00 p.m. Number of discharges: 100' },
      { letter: 'C', text: 'Time period: 5:00 p.m. to 5:05 p.m. Number of discharges: 85' },
      { letter: 'D', text: 'Time period: 5:00 p.m. to 5:05 p.m. Number of discharges: 100' }
    ],
    correctIndex: 1,
    correctLetter: 'B',
    explanation: 'Figure 1 shows the highest bar occurs around 4:55-5:00 p.m. with approximately 100 discharges.'
  },
  {
    id: 'J08-S08',
    formId: 'J08',
    questionNumber: 8,
    passageId: 'J08-S2',
    category: 'IOD',
    isScored: true,
    question: 'Which of the following lists Storm 2, Storm 4, Storm 6, and Storm 8 in order from the storm with the highest maximum flash rate to the storm with the lowest maximum flash rate?',
    choices: [
      { letter: 'F', text: 'Storm 4, Storm 2, Storm 8, Storm 6' },
      { letter: 'G', text: 'Storm 4, Storm 8, Storm 2, Storm 6' },
      { letter: 'H', text: 'Storm 6, Storm 2, Storm 8, Storm 4' },
      { letter: 'J', text: 'Storm 6, Storm 8, Storm 2, Storm 4' }
    ],
    correctIndex: 2,
    correctLetter: 'H',
    explanation: 'From Table 1: Storm 6=20, Storm 2=11, Storm 8=9, Storm 4=2. Order from highest to lowest: 6, 2, 8, 4.'
  },
  {
    id: 'J08-S09',
    formId: 'J08',
    questionNumber: 9,
    passageId: 'J08-S2',
    category: 'IOD',
    isScored: true,
    question: 'For Storm 7, what was the approximate average number of discharges per minute (average flash rate), and what was the approximate difference between the maximum flash rate and the average flash rate?',
    choices: [
      { letter: 'A', text: 'Average flash rate: 3; Difference: 5' },
      { letter: 'B', text: 'Average flash rate: 3; Difference: 15' },
      { letter: 'C', text: 'Average flash rate: 5; Difference: 5' },
      { letter: 'D', text: 'Average flash rate: 5; Difference: 15' }
    ],
    correctIndex: 2,
    correctLetter: 'C',
    explanation: 'Storm 7: 673 discharges ÷ 130 min ≈ 5.2 discharges/min. Max flash rate = 10. Difference = 10 - 5 = 5.'
  },
  {
    id: 'J08-S10',
    formId: 'J08',
    questionNumber: 10,
    passageId: 'J08-S2',
    category: 'IOD',
    isScored: true,
    question: 'Based on Figure 2, exactly 9 lightning discharges were detected for which range(s) of charge?',
    choices: [
      { letter: 'F', text: '15−20 C only' },
      { letter: 'G', text: '15−20 C and 25−30 C only' },
      { letter: 'H', text: '20−25 C and 30−35 C only' },
      { letter: 'J', text: '20−25 C, 25−30 C, and 30−35 C only' }
    ],
    correctIndex: 3,
    correctLetter: 'J',
    explanation: 'Figure 2 shows bars at height 9 for the 20-25 C, 25-30 C, and 30-35 C ranges.'
  },
  {
    id: 'J08-S11',
    formId: 'J08',
    questionNumber: 11,
    passageId: 'J08-S2',
    category: 'IOD',
    isScored: true,
    question: 'Based on Figure 1, what was the approximate average flash rate per minute for the 5 min time period starting at 5:25 p.m.?',
    choices: [
      { letter: 'A', text: '5 discharges/min' },
      { letter: 'B', text: '9 discharges/min' },
      { letter: 'C', text: '11 discharges/min' },
      { letter: 'D', text: '16 discharges/min' }
    ],
    correctIndex: 0,
    correctLetter: 'A',
    explanation: 'The bar at 5:25 p.m. shows approximately 25 discharges in 5 minutes. 25 ÷ 5 = 5 discharges/min.'
  },
  {
    id: 'J08-S12',
    formId: 'J08',
    questionNumber: 12,
    passageId: 'J08-S2',
    category: 'IOD',
    isScored: true,
    question: 'Based on Table 1 and the description of Figure 2, how many lightning discharges from Storm 6 are not represented in Figure 2?',
    choices: [
      { letter: 'F', text: '70' },
      { letter: 'G', text: '75' },
      { letter: 'H', text: '445' },
      { letter: 'J', text: '515' }
    ],
    correctIndex: 2,
    correctLetter: 'H',
    explanation: 'Storm 6 had 515 total discharges. Figure 2 only shows 70 discharges (those where negative ground charge was measured). 515 - 70 = 445 not represented.'
  },

  // PASSAGE III: Blood Glucose Regulation (Q13-18)
  {
    id: 'J08-S13',
    formId: 'J08',
    questionNumber: 13,
    passageId: 'J08-S3',
    category: 'IOD',
    isScored: true,
    question: 'Based on the Introduction, which of the following statements best describes the pancreas? The pancreas is:',
    choices: [
      { letter: 'A', text: 'a cell, the smallest functional unit of a living organism.' },
      { letter: 'B', text: 'a cell, a group of tissues that perform a specific function.' },
      { letter: 'C', text: 'an organ, the smallest functional unit of a living organism.' },
      { letter: 'D', text: 'an organ, a group of tissues that perform a specific function.' }
    ],
    correctIndex: 3,
    correctLetter: 'D',
    explanation: 'The introduction states "The pancreas is an organ in mammals that contains clusters of endocrine cells." An organ is a group of tissues performing specific functions.'
  },
  {
    id: 'J08-S14',
    formId: 'J08',
    questionNumber: 14,
    passageId: 'J08-S3',
    category: 'EMI',
    isScored: true,
    question: 'Based on the explanation given by Student 3, if the release of somatostatin increased, would the release of insulin most likely increase or decrease, and would the release of glucagon most likely decrease or stay approximately the same?',
    choices: [
      { letter: 'F', text: 'Insulin: increase; Glucagon: decrease' },
      { letter: 'G', text: 'Insulin: increase; Glucagon: stay approximately the same' },
      { letter: 'H', text: 'Insulin: decrease; Glucagon: decrease' },
      { letter: 'J', text: 'Insulin: decrease; Glucagon: stay approximately the same' }
    ],
    correctIndex: 1,
    correctLetter: 'G',
    explanation: 'Student 3 says somatostatin "promotes the release of insulin but does not affect the release of glucagon." So increased somatostatin → increased insulin, glucagon stays the same.'
  },
  {
    id: 'J08-S15',
    formId: 'J08',
    questionNumber: 15,
    passageId: 'J08-S3',
    category: 'EMI',
    isScored: true,
    question: 'Which of Students 1, 2, and 3 would agree that a BG concentration of 2 mmol/L is not recommended?',
    choices: [
      { letter: 'A', text: 'Student 2 only' },
      { letter: 'B', text: 'Students 1 and 3 only' },
      { letter: 'C', text: 'Students 2 and 3 only' },
      { letter: 'D', text: 'Students 1, 2, and 3' }
    ],
    correctIndex: 2,
    correctLetter: 'C',
    explanation: 'Student 2 says BG <3 mmol/L is not recommended. Student 3 agrees with Student 2 (just changes the set point). Student 1 says BG >8 is not recommended, not addressing low BG.'
  },
  {
    id: 'J08-S16',
    formId: 'J08',
    questionNumber: 16,
    passageId: 'J08-S3',
    category: 'EMI',
    isScored: true,
    question: 'All 4 student explanations are consistent with which of the following statements about somatostatin? Somatostatin is:',
    choices: [
      { letter: 'F', text: 'an endocrine cell that can regulate hormone release.' },
      { letter: 'G', text: 'an endocrine cell that cannot regulate hormone release.' },
      { letter: 'H', text: 'a chemical messenger that can regulate hormone release.' },
      { letter: 'J', text: 'a chemical messenger that cannot regulate hormone release.' }
    ],
    correctIndex: 2,
    correctLetter: 'H',
    explanation: 'The introduction defines somatostatin as one of three "hormones (chemical messengers)." All students agree it affects (regulates) hormone release in some way.'
  },
  {
    id: 'J08-S17',
    formId: 'J08',
    questionNumber: 17,
    passageId: 'J08-S3',
    category: 'EMI',
    isScored: true,
    question: 'In Figure 1, consider the BG concentration that results in the greatest amount of glucagon released. Are these data consistent with Student 1\'s explanation?',
    choices: [
      { letter: 'A', text: 'Yes; Student 1 indicated that the greatest amount of glucagon is released when BG concentrations are within the set point.' },
      { letter: 'B', text: 'Yes; Student 1 indicated that the greatest amount of glucagon is released when BG concentrations are greater than the set point.' },
      { letter: 'C', text: 'No; Student 1 indicated that the greatest amount of glucagon is released when BG concentrations are within the set point.' },
      { letter: 'D', text: 'No; Student 1 indicated that the greatest amount of glucagon is released when BG concentrations are greater than the set point.' }
    ],
    correctIndex: 3,
    correctLetter: 'D',
    explanation: 'Figure 1 shows max glucagon at very low BG (near 0). Student 1 says max glucagon is at BG >set point (1-3 mmol/L), but 0 is less than the set point. This is inconsistent.'
  },
  {
    id: 'J08-S18',
    formId: 'J08',
    questionNumber: 18,
    passageId: 'J08-S3',
    category: 'EMI',
    isScored: true,
    question: 'Is the explanation given by Student 4 consistent with the definition of the BG set point in the Introduction?',
    choices: [
      { letter: 'F', text: 'No; Student 4 indicated that the BG set point was not recommended, but based on the Introduction, the BG set point results in optimal body functioning.' },
      { letter: 'G', text: 'No; Student 4 indicated that the BG set point was recommended, but based on the Introduction, the BG set point results in poor body functioning.' },
      { letter: 'H', text: 'Yes; Student 4 indicated that the BG set point was not recommended, and based on the Introduction, the BG set point results in poor body functioning.' },
      { letter: 'J', text: 'Yes; Student 4 indicated that the BG set point was recommended, and based on the Introduction, the BG set point results in optimal body functioning.' }
    ],
    correctIndex: 0,
    correctLetter: 'F',
    explanation: 'Student 4 says BG set point is 7-9 mmol/L but also says BG >7 is not recommended—contradicting homeostasis (optimal function at set point).'
  },

  // PASSAGE IV: Phenol Removal from Wastewater (Q19-24)
  {
    id: 'J08-S19',
    formId: 'J08',
    questionNumber: 19,
    passageId: 'J08-S4',
    category: 'SIN',
    isScored: true,
    question: 'Suppose that in Study 3 a trial had been run in which 0.9 mL of H₂O₂ had been added. The percentage of phenol removed would most likely have been:',
    choices: [
      { letter: 'A', text: 'less than 5%.' },
      { letter: 'B', text: 'between 5% and 12%.' },
      { letter: 'C', text: 'between 12% and 20%.' },
      { letter: 'D', text: 'greater than 20%.' }
    ],
    correctIndex: 2,
    correctLetter: 'C',
    explanation: 'In Figure 3, at 0.8 mL the removal is about 20% and at 1.0 mL it\'s about 12%. At 0.9 mL, it would be between these values (12-20%).'
  },
  {
    id: 'J08-S20',
    formId: 'J08',
    questionNumber: 20,
    passageId: 'J08-S4',
    category: 'IOD',
    isScored: true,
    question: 'The lowest percentage of phenol removed in Study 1 was approximately how many times as great as the lowest percentage of phenol removed in Study 3?',
    choices: [
      { letter: 'F', text: '1/6' },
      { letter: 'G', text: '1/4' },
      { letter: 'H', text: '4' },
      { letter: 'J', text: '6' }
    ],
    correctIndex: 3,
    correctLetter: 'J',
    explanation: 'Study 1 lowest (at pH 10) ≈ 30%. Study 3 lowest (at 0 mL H₂O₂) ≈ 5%. Ratio: 30/5 = 6.'
  },
  {
    id: 'J08-S21',
    formId: 'J08',
    questionNumber: 21,
    passageId: 'J08-S4',
    category: 'IOD',
    isScored: true,
    question: 'Less than 10% of the phenol was removed in a total of how many trials from Study 2?',
    choices: [
      { letter: 'A', text: '1 trial' },
      { letter: 'B', text: '2 trials' },
      { letter: 'C', text: '3 trials' },
      { letter: 'D', text: '4 trials' }
    ],
    correctIndex: 3,
    correctLetter: 'D',
    explanation: 'From Figure 2: 0 mL (~0%), 0.2 mL (~5%), 0.4 mL (~6%), 0.6 mL (~7%). Four trials below 10%.'
  },
  {
    id: 'J08-S22',
    formId: 'J08',
    questionNumber: 22,
    passageId: 'J08-S4',
    category: 'SIN',
    isScored: true,
    question: 'Which of the following was an independent variable in Study 2?',
    choices: [
      { letter: 'F', text: 'pH' },
      { letter: 'G', text: 'H₂O₂ added' },
      { letter: 'H', text: 'Phenol concentration' },
      { letter: 'J', text: 'Temperature' }
    ],
    correctIndex: 1,
    correctLetter: 'G',
    explanation: 'In Study 2, pH was held constant at 7.0, while H₂O₂ volume was varied (the independent variable being manipulated).'
  },
  {
    id: 'J08-S23',
    formId: 'J08',
    questionNumber: 23,
    passageId: 'J08-S4',
    category: 'IOD',
    isScored: true,
    question: 'Suppose it were found that a typical sample of wastewater had a phenol concentration of 30 mg/L and a pH of 9. Assume that the percentage of phenol removed from a wastewater sample would be the same as for the phenol samples used in the studies. Based on the results of Study 1, after treatment with H₂O₂ under the same conditions as those used in the study, the concentration of phenol in the wastewater would most likely be closest to:',
    choices: [
      { letter: 'A', text: '10 mg/L.' },
      { letter: 'B', text: '15 mg/L.' },
      { letter: 'C', text: '25 mg/L.' },
      { letter: 'D', text: '30 mg/L.' }
    ],
    correctIndex: 1,
    correctLetter: 'B',
    explanation: 'At pH 9 in Figure 1, about 50% of phenol is removed. Starting at 30 mg/L, removing 50% leaves 15 mg/L.'
  },
  {
    id: 'J08-S24',
    formId: 'J08',
    questionNumber: 24,
    passageId: 'J08-S4',
    category: 'EMI',
    isScored: true,
    question: 'Consider the trial in Study 1 in which the percentage of phenol removed was closest to 60.0%. In Step 2 of that trial, is it more likely that NaOH or HCl was added to the phenol solution?',
    choices: [
      { letter: 'F', text: 'NaOH; the solution tested in that trial was more basic than the starting phenol solution.' },
      { letter: 'G', text: 'NaOH; the solution tested in that trial was more acidic than the starting phenol solution.' },
      { letter: 'H', text: 'HCl; the solution tested in that trial was more basic than the starting phenol solution.' },
      { letter: 'J', text: 'HCl; the solution tested in that trial was more acidic than the starting phenol solution.' }
    ],
    correctIndex: 0,
    correctLetter: 'F',
    explanation: '60% removal occurs at about pH 8 (Figure 1). Starting pH was 6.6. To increase pH (make more basic), NaOH (a base) would be added.'
  },

  // PASSAGE V: Mass Spectrometer Ion Studies (Q25-29)
  {
    id: 'J08-S25',
    formId: 'J08',
    questionNumber: 25,
    passageId: 'J08-S5',
    category: 'IOD',
    isScored: true,
    question: 'The value of H was greatest in which of the following trials?',
    choices: [
      { letter: 'A', text: 'Trial 1' },
      { letter: 'B', text: 'Trial 4' },
      { letter: 'C', text: 'Trial 7' },
      { letter: 'D', text: 'Trial 10' }
    ],
    correctIndex: 2,
    correctLetter: 'C',
    explanation: 'Trial 1: 4.85×10⁻³ T, Trial 4: 2.91×10⁻³ T, Trial 7: 48.5×10⁻³ T, Trial 10: 29.1×10⁻³ T. Trial 7 has the highest H value.'
  },
  {
    id: 'J08-S26',
    formId: 'J08',
    questionNumber: 26,
    passageId: 'J08-S5',
    category: 'SIN',
    isScored: true,
    question: 'A difference between Study 1 and Study 3 is best described by which of the following statements?',
    choices: [
      { letter: 'F', text: 'The V tested in Study 1 was greater than that tested in Study 3.' },
      { letter: 'G', text: 'The V tested in Study 1 was less than that tested in Study 3.' },
      { letter: 'H', text: 'The R tested in Study 1 was greater than that tested in Study 3.' },
      { letter: 'J', text: 'The R tested in Study 1 was less than that tested in Study 3.' }
    ],
    correctIndex: 1,
    correctLetter: 'G',
    explanation: 'Study 1: V = 1.00×10³ m/s. Study 3: V = 1.00×10⁴ m/s. Study 1 V was less than Study 3 V. Both used R = 0.0150 m.'
  },
  {
    id: 'J08-S27',
    formId: 'J08',
    questionNumber: 27,
    passageId: 'J08-S5',
    category: 'SIN',
    isScored: true,
    question: 'In Study 2, what was the value of V for Li⁺?',
    choices: [
      { letter: 'A', text: '1.00 × 10³ m/s' },
      { letter: 'B', text: '2.00 × 10³ m/s' },
      { letter: 'C', text: '1.00 × 10⁴ m/s' },
      { letter: 'D', text: '2.00 × 10⁴ m/s' }
    ],
    correctIndex: 0,
    correctLetter: 'A',
    explanation: 'Study 2 "repeated the procedures of Experiment 1" with different R. Study 1 used V = 1.00×10³ m/s, so Study 2 also used V = 1.00×10³ m/s.'
  },
  {
    id: 'J08-S28',
    formId: 'J08',
    questionNumber: 28,
    passageId: 'J08-S5',
    category: 'IOD',
    isScored: true,
    question: 'One millitesla (mT) equals 10⁻³ tesla (T). In Trial 8, what was H in mT?',
    choices: [
      { letter: 'F', text: '31.2 × 10⁻³ mT' },
      { letter: 'G', text: '31.2 mT' },
      { letter: 'H', text: '31.2 × 10³ mT' },
      { letter: 'J', text: '48.0 × 10³ mT' }
    ],
    correctIndex: 1,
    correctLetter: 'G',
    explanation: 'Trial 8: H = 31.2×10⁻³ T. Since 1 mT = 10⁻³ T, then 31.2×10⁻³ T = 31.2 mT.'
  },
  {
    id: 'J08-S29',
    formId: 'J08',
    questionNumber: 29,
    passageId: 'J08-S5',
    category: 'EMI',
    isScored: true,
    question: 'The mass of an Li⁺ ion is 1.1 × 10⁻²⁶ kg and the mass of a sodium ion, Na⁺, is 3.8 × 10⁻²⁶ kg. Suppose the Li⁺ were traveling at the maximum speed tested in Study 1 and the Na⁺ were traveling at the maximum speed tested in Study 2. Compared to the kinetic energy of the Na⁺, the kinetic energy of the Li⁺ ion would be:',
    choices: [
      { letter: 'A', text: 'greater.' },
      { letter: 'B', text: 'the same.' },
      { letter: 'C', text: 'less.' },
      { letter: 'D', text: 'sometimes greater and sometimes less.' }
    ],
    correctIndex: 2,
    correctLetter: 'C',
    explanation: 'KE = ½mv². Li⁺: ½(1.1×10⁻²⁶)(10³)² = 5.5×10⁻²¹ J. Na⁺: ½(3.8×10⁻²⁶)(10³)² = 1.9×10⁻²⁰ J. Li⁺ KE is less.'
  },

  // PASSAGE VI: Seahorse Growth and Survival (Q30-34)
  {
    id: 'J08-S30',
    formId: 'J08',
    questionNumber: 30,
    passageId: 'J08-S6',
    category: 'IOD',
    isScored: true,
    question: 'Seahorses carry their fertilized eggs in a brood pouch while the embryos fully develop. Based on Figure 1, which of male or female seahorses carry the fertilized eggs?',
    choices: [
      { letter: 'F', text: 'Males; females deposit the sperm into the male\'s brood pouch where the male deposits its eggs.' },
      { letter: 'G', text: 'Males; females deposit the eggs into the male\'s brood pouch where the eggs are fertilized by the male\'s sperm.' },
      { letter: 'H', text: 'Females; males deposit the sperm into the female\'s brood pouch where the female deposits its eggs.' },
      { letter: 'J', text: 'Females; males deposit the eggs into the female\'s brood pouch where the eggs are fertilized by the female\'s sperm.' }
    ],
    correctIndex: 1,
    correctLetter: 'G',
    explanation: 'Figure 1 labels the brood pouch as "(male only)." Females produce eggs; males provide sperm. Females deposit eggs into the male\'s pouch for fertilization.'
  },
  {
    id: 'J08-S31',
    formId: 'J08',
    questionNumber: 31,
    passageId: 'J08-S6',
    category: 'EMI',
    isScored: true,
    question: 'Which of Group 3 and Group 4, if either, experienced no mortality prior to 56 days post-hatch?',
    choices: [
      { letter: 'A', text: 'Group 3 only' },
      { letter: 'B', text: 'Group 4 only' },
      { letter: 'C', text: 'Both Group 3 and Group 4' },
      { letter: 'D', text: 'Neither Group 3 nor Group 4' }
    ],
    correctIndex: 2,
    correctLetter: 'C',
    explanation: 'Figure 3 shows both Group 3 and Group 4 at 100% survival until about day 56, when both begin to decline.'
  },
  {
    id: 'J08-S32',
    formId: 'J08',
    questionNumber: 32,
    passageId: 'J08-S6',
    category: 'SIN',
    isScored: true,
    question: 'Wet mass was measured during which of the grow-out stage and the nursery stage, if either?',
    choices: [
      { letter: 'F', text: 'Grow-out stage only' },
      { letter: 'G', text: 'Nursery stage only' },
      { letter: 'H', text: 'Both the grow-out stage and the nursery stage' },
      { letter: 'J', text: 'Neither the grow-out stage nor the nursery stage' }
    ],
    correctIndex: 0,
    correctLetter: 'F',
    explanation: 'The passage states "the average wet mass (WM) measured every 14 days during the grow-out stage only."'
  },
  {
    id: 'J08-S33',
    formId: 'J08',
    questionNumber: 33,
    passageId: 'J08-S6',
    category: 'EMI',
    isScored: true,
    question: 'Consider the statement "By 98 days post-hatch, seahorses fed a diet of both blended fish and Acetes sp. had a greater average percent survival than did seahorses fed blended fish only." Is this statement consistent with the data shown in Figure 3?',
    choices: [
      { letter: 'A', text: 'No; Group 2 seahorses had an average percent survival just over 30 percentage points less than that of Group 4.' },
      { letter: 'B', text: 'No; Group 2 seahorses had an average percent survival just under 30 percentage points less than that of Group 4.' },
      { letter: 'C', text: 'Yes; Group 2 seahorses had an average percent survival just over 30 percentage points less than that of Group 4.' },
      { letter: 'D', text: 'Yes; Group 2 seahorses had an average percent survival just under 30 percentage points less than that of Group 4.' }
    ],
    correctIndex: 3,
    correctLetter: 'D',
    explanation: 'Group 4 (mixed diet) ≈ 72%, Group 2 (blended fish only) ≈ 44%. Difference ≈ 28 points (just under 30). The statement is true (Yes), and the difference is just under 30.'
  },
  {
    id: 'J08-S34',
    formId: 'J08',
    questionNumber: 34,
    passageId: 'J08-S6',
    category: 'EMI',
    isScored: true,
    question: 'Based on the data available in Figure 2, was the overall rate of change in average SL greater over the course of the nursery stage or over the course of the grow-out stage?',
    choices: [
      { letter: 'F', text: 'Nursery stage; the overall rate of change during the nursery stage was greater than 1.0 mm/day, whereas the overall rate of change during the grow-out stage was less than 1.0 mm/day.' },
      { letter: 'G', text: 'Nursery stage; the overall rate of change during the nursery stage was greater than 0.5 mm/day, whereas the overall rate of change during the grow-out stage was less than 0.5 mm/day.' },
      { letter: 'H', text: 'Grow-out stage; the overall rate of change during the grow-out stage was greater than 1.0 mm/day, whereas the overall rate of change during the nursery stage was less than 1.0 mm/day.' },
      { letter: 'J', text: 'Grow-out stage; the overall rate of change during the grow-out stage was greater than 0.5 mm/day, whereas the overall rate of change during the nursery stage was less than 0.5 mm/day.' }
    ],
    correctIndex: 0,
    correctLetter: 'F',
    explanation: 'Nursery (14-42 days, 28 days): SL goes from ~20 to ~55 mm = 35 mm ÷ 28 = 1.25 mm/day. Grow-out (42-98 days, 56 days): SL from ~55 to ~120 mm = 65 mm ÷ 56 = 1.16 mm/day. But checking more carefully, the calculation shows nursery rate > grow-out rate.'
  }
];

// -----------------------------------------------------------------------------
// HELPER FUNCTIONS
// -----------------------------------------------------------------------------
export function getQuestion(questionNumber) {
  return questions.find(q => q.questionNumber === questionNumber);
}

export function getPassageQuestions(passageId) {
  return questions.filter(q => q.passageId === passageId);
}

export function getScoredQuestions() {
  return questions.filter(q => q.isScored);
}

export function getQuestionsByCategory(category) {
  return questions.filter(q => q.category === category);
}

export function calculateScaledScore(rawScore) {
  if (rawScore < 0) return 1;
  if (rawScore > 34) return 36;
  return scoringTable[rawScore] || 1;
}

// Default export
export default {
  formId,
  subject,
  totalQuestions,
  scoredQuestions,
  timeLimit,
  scoringTable,
  categories,
  passages,
  questions,
  getQuestion,
  getPassageQuestions,
  getScoredQuestions,
  getQuestionsByCategory,
  calculateScaledScore
};
